import React, { useState, useEffect, useMemo, useRef } from 'react';
import LocationForm from './LocationForm';
import CSVImport from './CSVImport';
import DistanceBatchCalculator from './DistanceBatchCalculator';
import { PageHeader } from './common';
import { API_ENDPOINTS } from '../utils/apiConfig';
import { useNotifications } from '../contexts/NotificationContext';

const PAGE_SIZE = 24; // Nombre de lieux par page

const LocationsList = ({ token, onNavigateToClients }) => {
  const { showSuccess, showError } = useNotifications();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showDistanceCalc, setShowDistanceCalc] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterGeocoding, setFilterGeocoding] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
  const listRef = useRef(null);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.LOCATIONS}?active_only=false`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setLocations(data.data);
        setError('');
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const deleteLocation = (locationId, locationName) => {
    setConfirmModal({
      show: true,
      title: 'Supprimer le lieu',
      message: `Êtes-vous sûr de vouloir supprimer le lieu "${locationName}" ?`,
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, show: false });
        try {
          const response = await fetch(API_ENDPOINTS.LOCATION_BY_ID(locationId), {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          const data = await response.json();

          if (data.success) {
            await loadLocations();
            showSuccess(data.message);
          } else {
            showError('Erreur: ' + data.error);
          }
        } catch (err) {
          showError('Erreur de connexion au serveur');
        }
      }
    });
  };

  const filteredLocations = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();
    return locations.filter(location => {
      // Recherche sur : nom, code, ville, CP, adresse, notes
      const matchesSearch = !search ||
                           location.name?.toLowerCase().includes(search) ||
                           location.code?.toLowerCase().includes(search) ||
                           location.city?.toLowerCase().includes(search) ||
                           location.postal_code?.toLowerCase().includes(search) ||
                           location.address?.toLowerCase().includes(search) ||
                           location.notes?.toLowerCase().includes(search);
      const matchesStatus = filterStatus === '' ||
                           (filterStatus === 'active' && location.is_active) ||
                           (filterStatus === 'inactive' && !location.is_active);
      const matchesGeocoding = filterGeocoding === '' ||
                              (filterGeocoding === 'issues' && (location.geocoding_status === 'warning' || location.geocoding_status === 'error')) ||
                              (filterGeocoding === 'warning' && location.geocoding_status === 'warning') ||
                              (filterGeocoding === 'error' && location.geocoding_status === 'error') ||
                              (filterGeocoding === 'ok' && location.geocoding_status === 'ok') ||
                              (filterGeocoding === 'pending' && (!location.geocoding_status || location.geocoding_status === 'pending'));

      return matchesSearch && matchesStatus && matchesGeocoding;
    });
  }, [locations, searchTerm, filterStatus, filterGeocoding]);

  // Pagination
  const totalPages = Math.ceil(filteredLocations.length / PAGE_SIZE);
  const paginatedLocations = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredLocations.slice(start, start + PAGE_SIZE);
  }, [filteredLocations, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterGeocoding]);

  // Scroll to top of list when page changes
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage]);

  useEffect(() => {
    loadLocations();
  }, [token]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Chargement des lieux de mise...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <PageHeader
        icon="🍇"
        title="Lieux de Mise"
        color="teal"
        actions={
          <>
            {onNavigateToClients && (
              <button
                onClick={onNavigateToClients}
                style={{
                  background: 'transparent',
                  color: '#fd7e14',
                  border: '2px solid #fd7e14',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#fd7e14';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#fd7e14';
                }}
              >
                Clients
              </button>
            )}
            <button
              onClick={() => setShowDistanceCalc(true)}
              style={{
                background: 'linear-gradient(135deg, #28a745, #20c997)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Calculer KM
            </button>
            <button
              onClick={() => setShowImport(true)}
              style={{
                background: 'linear-gradient(135deg, #6c757d, #5a6268)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Import CSV
            </button>
            <button
              onClick={() => { setEditingLocation(null); setShowForm(true); }}
              style={{
                background: 'linear-gradient(135deg, #17a2b8, #138496)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              + Nouveau lieu
            </button>
          </>
        }
      />

      {error && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Filtres */}
      <div style={{
        background: 'white',
        padding: 'clamp(15px, 4vw, 20px)',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        marginBottom: '20px'
      }}>
        <div style={{
          display: 'flex',
          gap: 'clamp(10px, 3vw, 15px)',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Recherche */}
          <input
            type="text"
            placeholder="🔍 Nom, ville, CP, code, adresse..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '280px',
              padding: '10px 14px',
              border: '2px solid #e1e5e9',
              borderRadius: '10px',
              fontSize: '14px',
              fontFamily: 'inherit',
              transition: 'border-color 0.2s ease',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#17a2b8'}
            onBlur={(e) => e.target.style.borderColor = '#e1e5e9'}
          />

          {/* Filtre statut */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '2px solid #e1e5e9',
              borderRadius: '8px',
              fontSize: 'clamp(13px, 2.5vw, 14px)',
              minWidth: '140px'
            }}
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </select>

          <select
            value={filterGeocoding}
            onChange={(e) => setFilterGeocoding(e.target.value)}
            style={{
              padding: '10px 12px',
              border: filterGeocoding === 'issues' || filterGeocoding === 'error' ? '2px solid #dc3545' :
                      filterGeocoding === 'warning' ? '2px solid #ffc107' :
                      filterGeocoding === 'ok' ? '2px solid #28a745' : '2px solid #e1e5e9',
              borderRadius: '8px',
              fontSize: 'clamp(13px, 2.5vw, 14px)',
              minWidth: '160px',
              background: filterGeocoding === 'issues' || filterGeocoding === 'error' ? '#fff5f5' :
                          filterGeocoding === 'warning' ? '#fffbeb' :
                          filterGeocoding === 'ok' ? '#f0fff4' : 'white'
            }}
          >
            <option value="">Validation adresse</option>
            <option value="issues">A corriger</option>
            <option value="error">Erreurs</option>
            <option value="warning">Avertissements</option>
            <option value="ok">Validees</option>
            <option value="pending">Non verifiees</option>
          </select>

          {/* Compteur résultats */}
          <span style={{ marginLeft: 'auto', color: '#666', fontSize: '14px', fontWeight: '500' }}>
            {filteredLocations.length} lieu(x)
          </span>
        </div>
      </div>

      {/* Liste des lieux */}
      <div ref={listRef} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 'clamp(15px, 4vw, 20px)', scrollMarginTop: '80px' }}>
        {paginatedLocations.map(location => (
          <div
            key={location.id}
            style={{
              background: 'white',
              borderRadius: '16px',
              padding: 'clamp(16px, 4vw, 20px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: location.is_active ? '2px solid transparent' : '2px solid #dc3545',
              transition: 'all 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
            }}
          >
            {/* Ligne de badges */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              marginBottom: '12px',
              alignItems: 'center'
            }}>
              {/* Badge Actif/Inactif */}
              <span style={{
                background: location.is_active
                  ? 'linear-gradient(135deg, #28a745, #20c997)'
                  : 'linear-gradient(135deg, #dc3545, #c82333)',
                color: 'white',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                {location.is_active ? '✅ Actif' : '❌ Inactif'}
              </span>

              {/* Badge Code */}
              <span style={{
                background: '#e3f2fd',
                color: '#1565c0',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                {location.code}
              </span>

              {/* Badge Distance */}
              {location.distance_from_depot && (
                <span style={{
                  background: '#d4edda',
                  color: '#155724',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  🚛 {location.distance_from_depot} km
                </span>
              )}

              {/* Badge Géocodage */}
              {location.geocoding_status === 'error' && (
                <span style={{
                  background: 'linear-gradient(135deg, #dc3545, #c82333)',
                  color: 'white',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600'
                }} title={`Écart: ${location.geocoding_discrepancy_m}m`}>
                  ⚠️ Adresse invalide
                </span>
              )}
              {location.geocoding_status === 'warning' && (
                <span style={{
                  background: '#ffc107',
                  color: '#212529',
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: '600'
                }} title={`Écart: ${location.geocoding_discrepancy_m}m`}>
                  ⚠️ À vérifier
                </span>
              )}
            </div>

            {/* Nom */}
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: 'clamp(16px, 3vw, 18px)',
              fontWeight: '600',
              color: '#333'
            }}>
              {location.name}
            </h3>

            {/* Informations */}
            <div style={{ marginBottom: '16px', fontSize: 'clamp(13px, 2.5vw, 14px)', flex: 1 }}>
              {location.city && (
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📍</span>
                  <span style={{ color: '#666' }}>
                    {location.city}{location.postal_code && ` (${location.postal_code})`}
                  </span>
                </div>
              )}
              {location.phone && (
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📞</span>
                  <span style={{ color: '#666' }}>{location.phone}</span>
                </div>
              )}
              {location.contact_name && (
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>👤</span>
                  <span style={{ color: '#666' }}>{location.contact_name}</span>
                </div>
              )}
              {location.contact_email && (
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📧</span>
                  <span style={{ color: '#666', wordBreak: 'break-word' }}>{location.contact_email}</span>
                </div>
              )}
              {location.client_count > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🏢</span>
                  <span style={{ color: '#666' }}>{location.client_count} client(s) associé(s)</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setEditingLocation(location); setShowForm(true); }}
                style={{
                  padding: '8px 16px',
                  background: 'linear-gradient(135deg, #007bff, #0056b3)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <span>✏️</span>
                <span>Modifier</span>
              </button>
              <button
                onClick={() => deleteLocation(location.id, location.name)}
                style={{
                  padding: '8px 16px',
                  background: 'linear-gradient(135deg, #dc3545, #c82333)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <span>🗑️</span>
                <span>Supprimer</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginTop: '24px',
          padding: '16px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              background: currentPage === 1 ? '#e9ecef' : '#17a2b8',
              color: currentPage === 1 ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            ««
          </button>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              background: currentPage === 1 ? '#e9ecef' : '#17a2b8',
              color: currentPage === 1 ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            Précédent
          </button>

          <span style={{ padding: '0 16px', fontWeight: '600', color: '#495057' }}>
            Page {currentPage} / {totalPages}
          </span>
          <span style={{ color: '#6c757d', fontSize: '14px' }}>
            ({filteredLocations.length} lieux)
          </span>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px',
              background: currentPage === totalPages ? '#e9ecef' : '#17a2b8',
              color: currentPage === totalPages ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            Suivant
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              background: currentPage === totalPages ? '#e9ecef' : '#17a2b8',
              color: currentPage === totalPages ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            »»
          </button>
        </div>
      )}

      {filteredLocations.length === 0 && (
        <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
          Aucun lieu de mise trouve
        </div>
      )}

      {/* Formulaire modal */}
      {showForm && (
        <LocationForm
          token={token}
          location={editingLocation}
          onClose={() => setShowForm(false)}
          onSave={() => {
            setShowForm(false);
            loadLocations();
          }}
        />
      )}

      {/* Import CSV modal */}
      {showImport && (
        <CSVImport
          token={token}
          importType="locations"
          apiEndpoint={API_ENDPOINTS.LOCATIONS_IMPORT}
          onClose={() => setShowImport(false)}
          onImportComplete={() => {
            loadLocations();
          }}
        />
      )}

      {/* Calcul distances modal */}
      {showDistanceCalc && (
        <DistanceBatchCalculator
          token={token}
          entityType="locations"
          onClose={() => setShowDistanceCalc(false)}
          onComplete={() => loadLocations()}
        />
      )}

      {/* Modal de confirmation */}
      {confirmModal.show && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 2000, padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '24px',
            maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>{confirmModal.title}</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#666' }}>{confirmModal.message}</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmModal({ ...confirmModal, show: false })}
                style={{ padding: '10px 20px', background: '#f5f5f5', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={confirmModal.onConfirm}
                style={{ padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsList;
